import app from '../app';
import Component from '../Component';
import extractText from '../utils/extractText';
import classList from '../utils/classList';
import Icon from './Icon';

/**
 * The `SearchSource` interface defines a tab of search results in the
 * search modal.
 *
 * Search sources should be registered with the `GlobalSearch` component class
 * by extending the `sourceItems` method. When the user types a
 * query, each search source will be prompted to load search results via the
 * `search` method. When the search modal's dropdown is redrawn, it will be constructed by
 * putting together the output from the `view` method of each source.
 */

/**
 * The `Search` component displays a primary search input at the top of the frontend (forum or admin).
 * When clicked, it opens an advanced search modal with results from various sources.
 *
 * Must be extended and the abstract methods implemented per-frontend.
 */
export default class AbstractGlobalSearch extends Component {
  /**
   * The instance of `SearchState` for this component.
   */

  oninit(vnode) {
    super.oninit(vnode);
    this.searchState = this.attrs.state;
  }
  view() {
    // Hide the search view if no sources were loaded
    if (this.sourceItems().isEmpty()) return m("div", null);
    const value = this.searchState.getValue();

    // Search happens in a modal, so this control does not accept text: it is a
    // button that opens the modal, and reads as one to assistive technology
    // rather than as a textbox that refuses input. Where its label is shown —
    // the drawer — it displays the current query in place of the prompt, so a
    // search that has been run stays visible after the modal closes.
    const openSearchModal = () => {
      app.modal.show(() => import(/* webpackChunkName: 'common/components/SearchModal', webpackMode: 'lazy-once' */ '../../common/components/SearchModal'), {
        searchState: this.searchState,
        sources: this.sourceItems().toArray()
      });
    };
    return m("div", {
      role: "search",
      className: classList('Search', {
        'Search--active': !!value
      }),
      "aria-label": this.attrs.a11yRoleLabel
    }, m("button", {
      type: "button"
      // `Button--flat` rather than `Button--link`: this sits among the
      // notification and message controls, which are flat buttons, and
      // should pick up the same hover and focus treatment as them.
      ,
      className: "Search-input Button Button--flat",
      "aria-label": value ? extractText(app.translator.trans('core.lib.search.search_active_button_accessible_label', {
        query: value
      })) : extractText(this.attrs.label),
      title: value || extractText(this.attrs.label),
      onclick: () => {
        // On phones the search button lives inside the slide-out drawer. Close the drawer as soon as
        // search is activated, before the modal is shown, so it isn't left open behind (and overlapping)
        // the full-screen search modal as it animates in. Hiding it here rather than in `openSearchModal`
        // lets the drawer finish sliding out during the delay below. No-op where the drawer is never open.
        app.drawer.hide();
        setTimeout(() => openSearchModal(), 150);
      }
    }, m(Icon, {
      name: "fas fa-search",
      className: "Button-icon"
    }), m("span", {
      className: "Button-label"
    }, m("span", {
      className: "Button-labelText"
    }, value || this.attrs.label))), !!value && m("button", {
      type: "button",
      className: "Search-clear Button Button--icon Button--link",
      "aria-label": extractText(app.translator.trans('core.lib.search.search_clear_button_accessible_label')),
      onclick: () => this.searchState.clear()
    }, m(Icon, {
      name: "fas fa-times-circle"
    })));
  }

  /**
   * A list of search sources that can be used to query for search results.
   */
}
flarum.reg.add('core', 'common/components/AbstractGlobalSearch', AbstractGlobalSearch);flarum.reg.addChunkModule('485', '7085', 'core', 'common/components/SearchModal');flarum.reg.addChunkModule('485', '7085', 'core', 'common/components/SearchModal');